import React, { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useLocation } from 'react-router-dom';
import { createPageUrl } from '@/utils';
import { useLanguage } from '@/components/ui/LanguageContext';
import { motion } from 'framer-motion';
import {
  Play,
  Clock,
  BookOpen,
  Target,
  CheckCircle2,
  ChevronRight,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Skeleton } from '@/components/ui/skeleton';
import { listSeries, listUserProgress, listVideos } from '@/api/content';
import { useRequireAuthRedirect } from '@/hooks/useRequireAuthRedirect';

const watchedStampSrc = '/sityouzumi-v1.png';

function appendReturnTo(target, returnTo) {
  const separator = target.includes('?') ? '&' : '?';
  return `${target}${separator}returnTo=${encodeURIComponent(returnTo)}`;
}

export default function SeriesPage() {
  const { t } = useLanguage();
  const { canRenderProtectedContent, isLoadingAuth } = useRequireAuthRedirect();
  const [stampFailed, setStampFailed] = useState(false);
  const location = useLocation();
  const returnTo = `${location.pathname}${location.search}`;
  const urlParams = new URLSearchParams(window.location.search);
  const seriesId = urlParams.get('id');

  const { data: seriesList = [], isLoading: seriesLoading } = useQuery({
    queryKey: ['series', 'detail-list'],
    queryFn: async () => listSeries(),
    enabled: canRenderProtectedContent,
  });

  const series = useMemo(
    () => seriesList.find((item) => String(item.id) === String(seriesId)) || null,
    [seriesId, seriesList]
  );

  const { data: videoResponse, isLoading: videosLoading } = useQuery({
    queryKey: ['series-videos', seriesId],
    queryFn: async () => listVideos({ pageSize: 100, seriesId }),
    enabled: canRenderProtectedContent && !!seriesId,
  });

  const videos = useMemo(() => {
    const items = videoResponse?.items ?? [];
    return items
      .filter((video) => !seriesId || String(video.series_id) === String(seriesId))
      .sort((left, right) => (left.order_in_series || 0) - (right.order_in_series || 0));
  }, [seriesId, videoResponse?.items]);

  const { data: progress = [] } = useQuery({
    queryKey: ['user-progress', 'series', seriesId],
    queryFn: listUserProgress,
    enabled: canRenderProtectedContent && !!seriesId,
  });
  const progressByVideoId = useMemo(
    () => new Map(progress.map((item) => [String(item.video_id), item])),
    [progress]
  );
  const isVideoCompleted = (video) =>
    video.user_progress?.is_completed === true || progressByVideoId.get(String(video.id))?.is_completed === true;
  const completedCount = videos.filter(isVideoCompleted).length;
  const progressPercent = videos.length > 0 ? Math.round((completedCount / videos.length) * 100) : 0;
  const continueVideo = videos.find((video) => !isVideoCompleted(video)) || videos[0] || null;

  if (isLoadingAuth || !canRenderProtectedContent) {
    return null;
  }

  if (seriesLoading) {
    return (
      <div className="min-h-screen bg-black text-white p-8">
        <div className="max-w-5xl mx-auto space-y-8">
          <Skeleton className="h-64 w-full rounded-2xl bg-zinc-800" />
          <Skeleton className="h-8 w-1/2 bg-zinc-800" />
          <Skeleton className="h-4 w-full bg-zinc-800" />
          <Skeleton className="h-4 w-3/4 bg-zinc-800" />
        </div>
      </div>
    );
  }

  if (!series) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <p className="text-zinc-500">Series not found</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white pb-20">
      <div className="relative h-80 md:h-96 overflow-hidden">
        <img
          src={series.thumbnail_url || 'https://images.unsplash.com/photo-1522202176988-66273c2fd55f?w=1200&h=400&fit=crop'}
          alt={series.title}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/70 to-transparent" />

        <div className="absolute bottom-0 left-0 right-0 p-6 md:p-12">
          <div className="max-w-5xl mx-auto">
            <div className="flex items-center gap-3 mb-4">
              <Badge variant="outline" className="border-zinc-600">
                {t(series.genre) || series.genre}
              </Badge>
            </div>

            <h1 className="text-3xl md:text-4xl font-bold mb-4">{series.title}</h1>

            <div className="flex flex-wrap items-center gap-6 text-sm text-zinc-300">
              <div className="flex items-center gap-2">
                <BookOpen className="w-4 h-4" />
                {series.total_videos || videos.length} {t('videos')}
              </div>
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4" />
                {series.total_duration_minutes || 0} {t('minutes')}
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                {completedCount}/{videos.length} {t('completedVideos')}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 md:px-8 py-8">
        <div className="grid lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-8">
            <motion.section initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
              <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
                <Target className="w-5 h-5 text-indigo-400" />
                {t('aboutCourse')}
              </h2>
              <p className="text-zinc-400 leading-relaxed">{series.description}</p>
            </motion.section>

            <motion.section
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
            >
              <h2 className="text-xl font-bold mb-4">{t('videoList')}</h2>

              <div className="space-y-3">
                {videosLoading ? (
                  [...Array(5)].map((_, index) => (
                    <Skeleton key={index} className="h-20 w-full rounded-xl bg-zinc-800" />
                  ))
                ) : (
                  videos.map((video, index) => (
                    <motion.div
                      key={video.id}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.05 }}
                    >
                      <Link
                        to={appendReturnTo(createPageUrl(`VideoPlayer?id=${video.id}`), returnTo)}
                        state={{ returnTo }}
                        className="block group"
                      >
                        <div className="relative flex items-center gap-4 p-4 rounded-xl border transition-all bg-zinc-900/50 border-zinc-800 hover:border-indigo-500/50 hover:bg-zinc-900">
                          <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 bg-zinc-800 text-zinc-400">
                            <span className="font-semibold">{video.order_in_series || index + 1}</span>
                          </div>

                          <div className="relative w-24 h-14 flex-shrink-0 rounded-lg overflow-hidden">
                            <img
                              src={video.thumbnail_url || 'https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=96&h=56&fit=crop'}
                              alt={video.title}
                              className="w-full h-full object-cover transition-all duration-300"
                            />
                            {isVideoCompleted(video) && !stampFailed && (
                              <img
                                src={watchedStampSrc}
                                alt="Watched"
                                className="absolute left-0.5 top-0.5 z-10 w-[calc(40%-20px)] min-w-5 pointer-events-none drop-shadow-[0_2px_6px_rgba(0,0,0,0.35)]"
                                onError={() => setStampFailed(true)}
                              />
                            )}
                            <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                              <Play className="w-6 h-6 text-white" />
                            </div>
                          </div>

                          <div className="flex-1 min-w-0">
                            <h3 className="font-medium truncate text-white">{video.title}</h3>
                            <div className="flex items-center gap-3 mt-1 text-xs text-zinc-500">
                              <span className="flex items-center gap-1">
                                <Clock className="w-3 h-3" />
                                {video.duration_minutes} {t('minutes')}
                              </span>
                              {video.is_free && (
                                <Badge className="bg-emerald-500/20 text-emerald-400 border-0 text-xs">
                                  FREE
                                </Badge>
                              )}
                            </div>
                          </div>

                          <ChevronRight className="w-5 h-5 flex-shrink-0 transition-transform text-zinc-500 group-hover:translate-x-1 group-hover:text-indigo-400" />
                        </div>
                      </Link>
                    </motion.div>
                  ))
                )}
              </div>
            </motion.section>
          </div>

          <div className="lg:col-span-1">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="sticky top-8 space-y-6"
            >
              <div className="p-6 bg-gradient-to-br from-zinc-900 to-zinc-950 rounded-2xl border border-zinc-800">
                <h3 className="font-semibold mb-4">{t('progress')}</h3>

                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-zinc-400 text-sm">{completedCount}/{videos.length} {t('completedVideos')}</span>
                    <span className="text-indigo-400 font-bold">{progressPercent}%</span>
                  </div>
                  <Progress value={progressPercent} className="h-2 bg-zinc-800" />
                </div>

                {continueVideo && (
                  <Link
                    to={appendReturnTo(createPageUrl(`VideoPlayer?id=${continueVideo.id}`), returnTo)}
                    state={{ returnTo }}
                  >
                    <Button className="w-full mt-6 bg-indigo-600 hover:bg-indigo-700">
                      <Play className="w-4 h-4 mr-2 fill-current" />
                      {t('startLearning')}
                    </Button>
                  </Link>
                )}
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
}
